import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface PlantProgressTrendsChartProps {
  data: Array<{
    month: string;
    [key: string]: string | number; // Dynamic plant names as keys
  }>;
  plants: string[];
  loading?: boolean;
}

const PlantProgressTrendsChart: React.FC<PlantProgressTrendsChartProps> = ({ data, plants, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Progress Trends</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0 || !plants || plants.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Progress Trends</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No plant progress trend data available
        </div>
      </div>
    );
  }

  const formatMonth = (month: string) => {
    const date = new Date(month);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const colors = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
    '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
  ];

  const getPlantColor = (index: number) => colors[index % colors.length];

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Plant Progress Trends</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatMonth}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
              labelFormatter={(label) => `Month: ${formatMonth(label)}`}
            />
            <Legend />
            {plants.map((plant, index) => (
              <Line
                key={plant}
                type="monotone"
                dataKey={plant}
                stroke={getPlantColor(index)}
                strokeWidth={2}
                name={plant}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Plant Legend</h4>
          <div className="space-y-2">
            {plants.map((plant, index) => {
              const latestData = data[data.length - 1];
              const currentProgress = latestData ? latestData[plant] as number : 0;
              return (
                <div key={plant} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{ backgroundColor: getPlantColor(index) }}
                    ></div>
                    <span className="text-sm font-medium">{plant}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{currentProgress.toFixed(1)}%</span>
                    <span className="ml-2 text-xs text-gray-500">current</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Trend Analysis</h4>
          <div className="space-y-3">
            {plants.map((plant, index) => {
              const firstData = data[0];
              const lastData = data[data.length - 1];
              const firstProgress = firstData ? firstData[plant] as number : 0;
              const lastProgress = lastData ? lastData[plant] as number : 0;
              const trend = lastProgress - firstProgress;
              const isImproving = trend > 0;
              
              return (
                <div key={plant} className={`p-3 rounded-lg ${isImproving ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                      {plant}
                    </span>
                    <span className={`font-bold ${isImproving ? 'text-green-600' : 'text-red-600'}`}>
                      {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                    </span>
                  </div>
                  <div className={`text-xs ${isImproving ? 'text-green-500' : 'text-red-500'} mt-1`}>
                    {isImproving ? 'Improving' : 'Declining'} over time
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantProgressTrendsChart;


